"use client";

import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import { TableSkeleton } from "@/components/common/TableSkeleton";
import { useAfiliasi, type AfiliasiData, type AfiliasiPerson } from "@/app/[lang]/about-us/affiliations/useAfiliasi";
import Image from "next/image";
import type { CSSProperties } from "react";
import React from "react";

const AFILIASI_KEYS: (keyof AfiliasiData)[] = [
  "keuangan_komisaris",
  "keuangan_direksi",
  "keuangan_pemegang_saham",
  "keluarga_komisaris",
  "keluarga_direksi",
  "keluarga_pemegang_saham",
  "kepengurusan",
];

const PERSON_TYPE_ORDER = ["komisaris", "direktur"];

export default function AffiliateTableSection() {
  const dict = useDict();
  const { data: afiliasiData, isLoading } = useAfiliasi();

  const tableVars: CSSProperties = {
    ["--col1w" as string]: "220px",
    ["--col2w" as string]: "200px",
    ["--theadH" as string]: "132px",
  };

  const Check = () => (
    <Image src="/images/illustration/check-blue.svg" alt="check" width={12} height={12} />
  );

  const sectionLabelMap: Record<string, string> = {
    komisaris: dict.affiliations.colCommissioners as string,
    direktur: dict.affiliations.colDirectors as string,
  };

  const grouped: { type: string; label: string; people: AfiliasiPerson[] }[] = [];
  if (afiliasiData?.data) {
    const typeOrder = [
      ...PERSON_TYPE_ORDER,
      ...afiliasiData.data
        .map((person) => person.person_type)
        .filter((type) => !PERSON_TYPE_ORDER.includes(type))
        .filter((type, index, allTypes) => allTypes.indexOf(type) === index),
    ];

    typeOrder.forEach((type) => {
      const people = afiliasiData.data.filter((person) => person.person_type === type);
      if (people.length > 0) {
        grouped.push({
          type,
          label: sectionLabelMap[type] ?? type,
          people,
        });
      }
    });
  }

  return (
    <>
      {isLoading ? (
        <TableSkeleton columns={8} rows={6} />
      ) : (
        <div className="relative overflow-x-auto">
          <table className="aff-table border border-gray-200 text-sm min-w-max w-full" style={tableVars}>
            <thead className="bg-primary-700 text-white">
              <tr>
                <th rowSpan={3} className="th-sticky col-name py-3 px-2">{dict.affiliations.colName}</th>
                <th rowSpan={3} className="th-sticky col-role py-3 px-2">{dict.affiliations.colRole}</th>
                <th colSpan={6} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colFinancial}</th>
                <th colSpan={6} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colFamily}</th>
                <th colSpan={2} rowSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colManagement}</th>
              </tr>
              <tr>
                <th colSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colCommissioners}</th>
                <th colSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colDirectors}</th>
                <th colSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colShareholders}</th>
                <th colSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colCommissioners}</th>
                <th colSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colDirectors}</th>
                <th colSpan={2} className="bg-primary-700 th-top py-3 px-2">{dict.affiliations.colShareholders}</th>
              </tr>
              <tr>
                {Array.from({ length: 14 }).map((_, index) => (
                  <th key={index} className="th-top py-3 px-2 min-w-14">
                    {index % 2 === 0 ? dict.affiliations.yes : dict.affiliations.no}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {grouped.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  <tr>
                    <th colSpan={2} className="section-sticky td-sticky col-name">
                      {group.label}
                    </th>
                    <th colSpan={14} className="section-sticky td-sticky col-role" />
                  </tr>

                  {group.people.map((person, personIndex) => (
                    <tr key={personIndex}>
                      <th className="td-sticky col-name py-3 px-2 text-left">{person.name}</th>
                      <td className="td-sticky col-role py-3 px-2 text-left border-r">{person.position}</td>
                      {AFILIASI_KEYS.map((key) => {
                        const value = person.afiliasi[key];
                        return (
                          <React.Fragment key={key}>
                            <td className="px-2 border-r border-l text-center">
                              {value === true ? <Check /> : ""}
                            </td>
                            <td className="px-2 border-r border-l text-center">
                              {value === false ? <Check /> : ""}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style jsx>{`
        .aff-table { border-collapse: separate; border-spacing: 0; }
        .aff-table th, .aff-table td { background: #fff; white-space: nowrap; }
        .aff-table th { background: #124673; color: #fff; }
        .th-top { position: sticky; top: 0; z-index: 20; }

        .th-sticky.col-name {
          position: sticky; top: 0; left: 0; z-index: 40;
          min-width: var(--col1w); width: var(--col1w);
        }
        .th-sticky.col-role {
          position: sticky; top: 0; left: var(--col1w); z-index: 40;
          min-width: var(--col2w); width: var(--col2w);
        }

        .td-sticky.col-name {
          position: sticky; left: 0; z-index: 10;
          min-width: var(--col1w); width: var(--col1w); background: #fff;
        }
        .td-sticky.col-role {
          position: sticky; left: var(--col1w); z-index: 10;
          min-width: var(--col2w); width: var(--col2w); background: #fff;
        }

        .section-sticky {
          position: sticky;
          left: 0;
          top: var(--theadH);
          z-index: 15;
          background: #f3f4f6;
          color: #4b5563;
          text-align: left;
          padding: 0.75rem 0.5rem;
          border-top: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }
      `}</style>
    </>
  );
}